import { Component, ViewChild, AfterViewInit, OnInit, ElementRef, Inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSortModule, MatSort } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { Router, RouterModule } from "@angular/router";
import { SKUService, Sku } from "@kelyn-technologies/dsio-angular-lib/dsio-api";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { CommonModule } from "@angular/common";
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatCheckboxModule, MatCheckbox } from "@angular/material/checkbox";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { Observable } from "rxjs";
import { MatSelectModule, MatSelect } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { HttpErrorResponse } from "@angular/common/http";
import { MatSlideToggleModule, MatSlideToggle } from "@angular/material/slide-toggle";
import { FormsModule } from '@angular/forms';

@Component({
    selector: "app-skus",
    templateUrl: "./skus.component.html",
    imports: [
        MatButtonModule,
        MatTableModule,
        MatSortModule,
        MatExpansionModule,
        RouterModule,
        MatProgressBarModule,
        CommonModule,
        MatDialogModule,
        MatSnackBarModule,
    ],
    styleUrls: ["./skus.component.css"]
})
export class SkusComponent implements AfterViewInit, OnInit {
  displayedColumns: string[] = ["name", "description", "enabled"];
  skus: Sku[] = [];
  dataSource = new MatTableDataSource<Sku>([]);
  isloading: boolean = true;

  public selectedSku: string = "";

  editName: string = "";
  editDescription: string = "";
  editEnabled: boolean = false;
  editSolutions: string[] = [];

  constructor(
    private skuService: SKUService,
    public router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  @ViewChild(MatSort) sort!: MatSort;

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  ngOnInit(): void {
    this.handleGetSkus();
  }

  setSelected(row: Sku): void {
    this.selectedSku = row.sku_name ?? "";

    this.editName = row.sku_name ?? "";
    this.editDescription = row.description ?? "";
    this.editEnabled = !!row.enabled;
    this.editSolutions = Array.isArray(row.solutions) ? [...row.solutions] : [];
  }

  getSelected(skuName: string): Sku | null {
    return this.skus.find((row: Sku) => row.sku_name === skuName) || null;
  }

  navigateToDetails(skuId: string): void {
    if (!skuId) return;
    this.router.navigate(["/skus/sku-detail", skuId]);
  }

  handleAddNewSkuDialog(): void {
    const dialogRef = this.dialog.open(AddSkuDialog, {
      width: "240px",
      data: {
        name: "",
        description: "",
        enabled: false,
        solutions:[]
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.handleAddSku(result as Sku);
    });
  }

  async handleAddSku(sku: any): Promise<void> {
    this.skuService.createSku(sku).subscribe({
      next: () => {
        this.snackBar.open('SKU added', 'close');
        this.handleGetSkus();
      },
      error: (err: HttpErrorResponse) => {
        const msg = err?.message || err?.statusText || "Failed to add SKU";
        this.snackBar.open(msg, "close");
      },
    });
  }

  handleEditSkuDialog(): void {
    const sku = this.getSelected(this.selectedSku);

    if (!sku) {
      this.snackBar.open("Select a SKU first", "close");
      return;
    }

    const dialogRef = this.dialog.open(EditSkuDialog, {
      width: "240px",
      data: {
        sku_name: this.editName,
        description: this.editDescription,
        enabled: this.editEnabled,
        solutions: [...this.editSolutions],
      },
    });

    dialogRef.afterClosed().subscribe((result: Sku) => {
      if (!result) return;
      this.handleEditSku(result);
    });
  }

  handleEditSku(data: Sku): void {
    const skuId = data.sku_name;

    const request: Sku = {
      sku_name: skuId,
      description: data.description,
      enabled: !!data.enabled,
      solutions: data.solutions ?? [],
    };

    this.skuService.updateSku(skuId, request).subscribe({
      next: (res: any) => {
        const saved: Sku = (res?.sku as Sku) ?? request;

        this.skus = this.skus.map(s =>
          s.sku_name === skuId ? saved : s
        );
        this.dataSource.data = [...this.skus];

        this.editDescription = saved.description ?? "";
        this.editEnabled = !!saved.enabled;
        this.editSolutions = saved.solutions ?? [];

        this.snackBar.open('SKU updated', 'close');
        this.handleGetSkus();
      },
      error: (err) =>
        this.snackBar.open(err?.message || "Update failed", "close"),
    });
  }

  handleDeleteSkuDialog(): void {
    const sku = this.getSelected(this.selectedSku);

    if (!sku) {
      this.snackBar.open("Select a SKU first", "close");
      return;
    }

    const dialogRef = this.dialog.open(DeleteSkuDialog, {
      width: "342px",
      data: sku,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.handleDeleteSku(sku.sku_name);
      }
    });
  }

  async handleDeleteSku(skuName: string): Promise<void> {
    this.skuService.deleteSku(skuName).subscribe({
      next: () => {
        this.snackBar.open("SKU Deleted", "close");
        this.skus = this.skus.filter(u => u.sku_name !== skuName);
        this.dataSource.data = this.skus;
        if (this.selectedSku === skuName) this.selectedSku = "";
      },
      error: (err: any) => {
        const msg = err?.message || err?.statusText || "Delete failed";
        this.snackBar.open(msg, "close");
      },
    });
  }

  async handleGetSkus() {
    this.isloading = true;

    this.skuService.getSkus().subscribe({
      next: (result: any) => {
        this.skus = result?.skus ?? [];
        this.dataSource.data = this.skus;
        this.isloading = false;
      },
      error: (err: any) => {
        this.isloading = false;
        const msg = err?.message || err?.statusText || "Failed to load SKUs";
        this.snackBar.open(msg, "close");
      },
    });
  }
}

@Component({
    selector: "add-sku-dialog",
    templateUrl: "./add-sku-dialog.html",
    styleUrls: ["./skus.component.css"],
    imports: [
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        MatInputModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatSelectModule,
        MatOptionModule
    ]
})
export class AddSkuDialog {
  @ViewChild("skuNameInput") skuNameInput!: ElementRef;
  @ViewChild("descriptionInput") descriptionInput!: ElementRef;
  @ViewChild("enabledInput") enabledInput!: MatCheckbox;
  @ViewChild("solutionsSelect") solutionsSelect!: MatSelect;
  constructor(
    public dialogRef: MatDialogRef<AddSkuDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  addSku(): void {
    const raw = this.solutionsSelect?.value;
    const solutions = Array.isArray(raw) ? raw : (this.data.solutions ?? []);

    const result: Partial<Sku> = {
      sku_name: this.skuNameInput.nativeElement.value,
      description: this.descriptionInput.nativeElement.value,
      enabled: !!this.enabledInput.checked,
      solutions,
    };
    this.dialogRef.close(result);
  }
}

@Component({
    selector: "dsio-edit-user-dialog",
    templateUrl: "./edit-sku-dialog.html",
    styleUrls: ["./skus.component.css"],
    imports: [
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        MatInputModule,
        MatFormFieldModule,
        MatSlideToggleModule,
        FormsModule,
        MatSelectModule,
        MatOptionModule
    ]
})
export class EditSkuDialog {
  @ViewChild("skuNameInput") skuNameInput!: ElementRef;
  @ViewChild("descriptionInput") descriptionInput!: ElementRef;
  @ViewChild("enabledInput") enabledInput!: MatSlideToggle;
  @ViewChild("solutionsSelect") solutionsSelect!: MatSelect;

  constructor(
    public dialogRef: MatDialogRef<EditSkuDialog>,
    @Inject(MAT_DIALOG_DATA) public data: Sku
  ) {}

  toBoolean(v: any): boolean {
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  updateSku(): void {
    const raw = this.solutionsSelect?.value;
    const solutions = Array.isArray(raw) ? raw : (this.data.solutions ?? []);

    const result: Partial<Sku> = {
      sku_name: this.skuNameInput.nativeElement.value,
      description: this.descriptionInput.nativeElement.value,
      enabled: !!this.enabledInput.checked,
      solutions,
    };
    this.dialogRef.close(result);
  }
}

@Component({
    selector: "delete-sku-dialog",
    templateUrl: "./delete-sku-dialog.html",
    styleUrls: ["./skus.component.css"],
    imports: [
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        MatInputModule,
        MatFormFieldModule,
    ]
})
export class DeleteSkuDialog {
  role$!: Observable<any>;

  constructor(
    public dialogRef: MatDialogRef<DeleteSkuDialog>,
    @Inject(MAT_DIALOG_DATA) public data: Sku
  ){}

  cancel(): void {
    this.dialogRef.close(false);
  }

  confirmDelete(): void {
    this.dialogRef.close(true);
  }
}
